import React, { useMemo, useState } from 'react';
import {
  Text,
  Button,
  Badge,
  Checkbox,
  Spinner,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  PlayRegular,
  StopRegular,
  ReceiptMoneyRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import DatePicker from './DatePicker';
import PhaseCard from './PhaseCard';
import PhaseTimelineStrip from './PhaseTimelineStrip';
import { applyPhaseEdit, EditablePhase } from '../lib/phaseDates';
import type { TimelineSegmentInput } from '../lib/phaseTimeline';
import {
  addSection,
  saveSectionSchedule,
  activateSection,
  activateSections,
  startSection,
  stopSection,
  assignLineSection,
} from '../services/sectionService';
import type { OrderSection } from '../services/orderService';

// In-order phases ("sections") surface on the order detail page. Each section is
// a date-bounded group of the order's lines with its own window (inside the
// order window), tier-priced on its own dates, billed progressively (Activate),
// and started/stopped independently. Replaces the project=N-orders model.
//
// This panel is the phased view: a timeline overview, then one PhaseCard per
// section. Each card's body is a section-scoped ProductsSection (rich line rows +
// rich picker) supplied via renderPhaseProducts. Every line lives in a phase —
// there is no unscheduled bucket. Lines move between phases by dragging their grip
// handle onto a card (or via the per-line Move menu inside the rich rows).

/** The drag payload key — the Booqable line id set by ProductsSection's grip. */
const LINE_DND_TYPE = 'application/x-line-id';

/** Minimal shape of a live order line this panel needs for per-section counts
 *  (billable/empty gating). The rich rows themselves are rendered by the
 *  ProductsSection that renderPhaseProducts returns. */
export interface SectionLineView {
  id: string;
  name: string;
  quantity: number;
  totalInCents: number;
}

interface OrderSectionsProps {
  orderId: string;
  /** Order window (YYYY-MM-DD) — sections must sit inside it. */
  orderStartDate: string | null;
  orderEndDate: string | null;
  sections: OrderSection[];
  /** Live order lines (from Booqable), keyed by Booqable line id. */
  orderLines: SectionLineView[];
  /** Booqable line id → section id, from the order-detail payload's tags. */
  lineSectionMap: Record<string, string | null>;
  /** Refetch the order after any mutation. */
  onChanged: () => void;
  /** Renders a section-scoped ProductsSection (rich rows + picker) for a card. */
  renderPhaseProducts: (section: OrderSection) => React.ReactNode;
  /** Remove a phase. The parent confirms + deletes the phase's line items when
   *  it has any, and dissolves the order back to a flat single-window order when
   *  only one phase would remain. */
  onRequestRemovePhase: (section: OrderSection) => void;
  /** Reports the computed umbrella (min delivery → max pickup over dated phases,
   *  YYYY-MM-DD) on every schedule change — including the optimistic edit before
   *  the save round-trip — so the read-only order window updates instantly. */
  onUmbrellaChange?: (start: string | null, end: string | null) => void;
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', ...shorthands.gap('12px') },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  cards: { display: 'flex', flexDirection: 'column', ...shorthands.gap('12px') },
  dates: { display: 'flex', alignItems: 'flex-end', ...shorthands.gap('8px') },
  dateCol: { display: 'flex', flexDirection: 'column', ...shorthands.gap('2px') },
  dateLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  arrow: { color: tokens.colorNeutralForeground3, marginBottom: '6px' },
  actions: { display: 'flex', alignItems: 'center', ...shorthands.gap('8px'), flexWrap: 'wrap' },
  error: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  // Drop zone wrappers — a dashed brand outline + tint while a line hovers, so
  // staff see exactly which phase (or the bucket) will receive the drop.
  dropZone: {
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('2px', 'solid', 'transparent'),
    transitionProperty: 'border-color, background-color',
    transitionDuration: '120ms',
  },
  dropActive: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  // White Draft badge text so it reads on the brand-blue card header.
  draftBadge: {
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorNeutralForegroundOnBrand),
  },
});

// ISO/Date/YYYY-MM-DD conversions (UTC-anchored to avoid timezone drift).
const isoToYmd = (iso: string | null): string | null => (iso ? iso.slice(0, 10) : null);
const ymdToDate = (ymd: string | null): Date | undefined =>
  ymd ? new Date(`${ymd}T00:00:00.000Z`) : undefined;
const dateToYmd = (d: Date | null | undefined): string | null =>
  d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10) : null;
const ymdToIso = (ymd: string | null): string | null => (ymd ? `${ymd}T00:00:00.000Z` : null);
const fmtDate = (d?: Date) =>
  d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '';

const OrderSections: React.FC<OrderSectionsProps> = ({
  orderId,
  orderStartDate,
  orderEndDate,
  sections,
  orderLines,
  lineSectionMap,
  onChanged,
  renderPhaseProducts,
  onRequestRemovePhase,
  onUmbrellaChange,
}) => {
  const styles = useStyles();
  const [busy, setBusy] = useState<string | null>(null); // section id, line key, 'add', or 'batch'
  const [error, setError] = useState<string | null>(null);
  // Section ids checked for batch billing (one invoice, one PO).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which phase card is currently under a dragged line (section id).
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Local editable schedule, seeded from server sections. Edits cascade through
  // applyPhaseEdit (the contiguous swap-hinge chain), then persist on commit.
  const [schedule, setSchedule] = useState<EditablePhase[]>([]);
  React.useEffect(() => {
    setSchedule(
      [...sections]
        .sort((a, b) => a.sequence - b.sequence)
        .map((s) => ({
          id: s.id,
          phaseNumber: s.sequence,
          startDate: isoToYmd(s.starts_at),
          endDate: isoToYmd(s.stops_at),
          promoted: !!s.activated_at, // an activated (billed) section is locked
        })),
    );
  }, [sections]);

  // Report the umbrella (min delivery → max pickup over dated phases) up on every
  // schedule change — seed AND optimistic edit — so the read-only order window
  // tracks phase edits instantly, before the save round-trip lands. YYYY-MM-DD
  // strings compare lexicographically, so min/max are plain string reduces.
  React.useEffect(() => {
    if (!onUmbrellaChange) return;
    const dated = schedule.filter((p) => p.startDate && p.endDate);
    if (dated.length === 0) {
      onUmbrellaChange(null, null);
      return;
    }
    const start = dated.reduce((m, p) => (p.startDate! < m ? p.startDate! : m), dated[0].startDate!);
    const end = dated.reduce((m, p) => (p.endDate! > m ? p.endDate! : m), dated[0].endDate!);
    onUmbrellaChange(start, end);
  }, [schedule, onUmbrellaChange]);

  const linesBySection = useMemo(() => {
    const map: Record<string, SectionLineView[]> = {};
    for (const line of orderLines) {
      const sid = lineSectionMap[line.id];
      if (!sid) continue;
      (map[sid] ||= []).push(line);
    }
    return map;
  }, [orderLines, lineSectionMap]);

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  const commitEdit = async (
    sectionId: string,
    edge: 'start' | 'end',
    date: Date | null,
  ) => {
    const next = applyPhaseEdit(schedule, {
      phaseId: sectionId,
      edge,
      date: dateToYmd(date),
      mode: 'absorb',
    });
    setSchedule(next);
    await run(sectionId, () =>
      saveSectionSchedule(
        orderId,
        next.map((p) => ({ id: p.id, starts_at: ymdToIso(p.startDate), stops_at: ymdToIso(p.endDate) })),
      ),
    );
  };

  // Drop a dragged line onto a phase card to reassign it to that phase.
  const handleDrop = (targetSectionId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDropTarget(null);
    const lineId = e.dataTransfer.getData(LINE_DND_TYPE);
    if (!lineId) return;
    // No-op if it's already there.
    if ((lineSectionMap[lineId] ?? null) === targetSectionId) return;
    run(`move:${lineId}`, () => assignLineSection(orderId, lineId, targetSectionId));
  };

  const allowDrop = (key: string) => (e: React.DragEvent) => {
    // Only react to a line drag.
    if (!e.dataTransfer.types.includes(LINE_DND_TYPE)) return;
    e.preventDefault();
    if (dropTarget !== key) setDropTarget(key);
  };

  const toggleSelect = (id: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const billSelected = () =>
    run('batch', async () => {
      await activateSections(orderId, [...selected]);
      setSelected(new Set());
    });

  const ordered = [...sections].sort((a, b) => a.sequence - b.sequence);
  const hasFinalPickup = ordered.some((s) => (s.label || '') === 'Final pickup');
  // A phase is billable once it has items and hasn't been billed yet.
  const isBillable = (s: OrderSection) => !s.activated_at && (linesBySection[s.id]?.length || 0) > 0;
  const billableCount = ordered.filter(isBillable).length;

  // Timeline segments — undated sections are dropped by the strip itself. Status
  // is left unset (uniform accent): per the a11y call, Draft/Billed meaning rides
  // the card badge, not segment color.
  const segments: TimelineSegmentInput[] = ordered.map((s) => ({
    id: s.id,
    phaseNumber: s.sequence,
    label: s.label || `Phase ${s.sequence}`,
    startDate: isoToYmd(s.starts_at),
    endDate: isoToYmd(s.stops_at),
    promoted: !!s.activated_at,
    paid: !!s.activated_at,
  }));
  const anyDated = segments.some((s) => !!s.startDate && !!s.endDate);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text className={styles.title}>Phases</Text>
        <div className={styles.actions}>
          {billableCount > 1 && (
            <Button
              appearance="primary"
              size="small"
              icon={<ReceiptMoneyRegular />}
              disabled={busy !== null || selected.size === 0}
              onClick={billSelected}
            >
              {`Bill ${selected.size || ''} selected`.trim()}
            </Button>
          )}
          <Button
            appearance={billableCount > 1 ? 'secondary' : 'primary'}
            size="small"
            icon={<AddRegular />}
            disabled={busy !== null || hasFinalPickup}
            onClick={() => run('add', () => addSection(orderId, {}))}
          >
            Add phase
          </Button>
        </div>
      </div>

      {error && <Text className={styles.error}>{error}</Text>}

      {anyDated && (
        <PhaseTimelineStrip
          segments={segments}
          onBoundaryEdit={(phaseId, edge, ymd) => commitEdit(phaseId, edge, ymdToDate(ymd) ?? null)}
        />
      )}

      <div className={styles.cards}>
        {ordered.map((s) => {
          const local = schedule.find((p) => p.id === s.id);
          const activated = !!s.activated_at;
          const lines = linesBySection[s.id] || [];
          const isLast = s.sequence === ordered[ordered.length - 1]?.sequence;
          const working = busy === s.id;
          // Billed phases are frozen — no drops in/out.
          const isDropTarget = dropTarget === s.id && !activated;

          const footer = (
            <>
              {/* Single-phase shortcut. When more than one phase is billable,
                  use the header's batch "Bill selected" instead (one invoice),
                  so we don't offer a per-card bill that the backend would
                  reject for leaving a sibling's lines unaccounted-for. */}
              {!activated && billableCount <= 1 && (
                <Button
                  size="small"
                  icon={<ReceiptMoneyRegular />}
                  disabled={working || lines.length === 0}
                  onClick={() => run(s.id, () => activateSection(orderId, s.id))}
                >
                  Activate &amp; bill
                </Button>
              )}
              <Button
                size="small"
                icon={<PlayRegular />}
                disabled={working || lines.length === 0}
                onClick={() => run(s.id, () => startSection(orderId, s.id))}
              >
                Start
              </Button>
              <Button
                size="small"
                icon={<StopRegular />}
                disabled={working || lines.length === 0}
                onClick={() => run(s.id, () => stopSection(orderId, s.id))}
              >
                Stop
              </Button>
              {!activated && isLast && (
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  disabled={working}
                  onClick={() => onRequestRemovePhase(s)}
                >
                  Remove
                </Button>
              )}
            </>
          );

          return (
            <div
              key={s.id}
              className={mergeClasses(styles.dropZone, isDropTarget && styles.dropActive)}
              onDragOver={activated ? undefined : allowDrop(s.id)}
              onDragLeave={() => setDropTarget((t) => (t === s.id ? null : t))}
              onDrop={activated ? undefined : handleDrop(s.id)}
            >
              <PhaseCard
                title={s.label || `Phase ${s.sequence}`}
                selected={selected.has(s.id)}
                status={
                  activated ? (
                    <Badge appearance="filled" color="success">Billed</Badge>
                  ) : (
                    <Badge appearance="outline" color="informative" className={styles.draftBadge}>
                      Draft
                    </Badge>
                  )
                }
                headerActions={
                  billableCount > 1 && isBillable(s) ? (
                    <Checkbox
                      checked={selected.has(s.id)}
                      disabled={busy !== null}
                      onChange={(_, d) => toggleSelect(s.id, !!d.checked)}
                      aria-label={`Include ${s.label || `Phase ${s.sequence}`} in batch invoice`}
                    />
                  ) : undefined
                }
                footer={footer}
              >
                <div className={styles.dates}>
                  <div className={styles.dateCol}>
                    <Text className={styles.dateLabel}>Delivery</Text>
                    <DatePicker
                      size="medium"
                      value={ymdToDate(local?.startDate ?? null) ?? null}
                      disabled={activated || working}
                      onSelectDate={(d) => commitEdit(s.id, 'start', d)}
                      formatDate={fmtDate}
                      placeholder="Delivery"
                      style={{ width: '140px' }}
                    />
                  </div>
                  <ArrowRightRegular className={styles.arrow} />
                  <div className={styles.dateCol}>
                    <Text className={styles.dateLabel}>Pick up</Text>
                    <DatePicker
                      size="medium"
                      value={ymdToDate(local?.endDate ?? null) ?? null}
                      disabled={activated || working}
                      onSelectDate={(d) => commitEdit(s.id, 'end', d)}
                      formatDate={fmtDate}
                      placeholder="Pick up"
                      style={{ width: '140px' }}
                    />
                  </div>
                  {working && <Spinner size="tiny" />}
                </div>

                {/* Rich line rows + product picker, scoped to this section. */}
                {renderPhaseProducts(s)}
              </PhaseCard>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderSections;
